import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export default function GoogleOAuthCallback() {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      // Send error message to parent window
      window.opener?.postMessage({
        type: 'GOOGLE_OAUTH_ERROR',
        error
      }, window.location.origin);
      window.close();
      return;
    }

    if (code) {
      // Exchange code for tokens
      exchangeCodeForTokens(code);
    }
  }, [searchParams]);

  const exchangeCodeForTokens = async (code: string) => {
    try {
      const redirectUri = `${window.location.origin}/oauth/google-search-console`;

      // Get the current session to pass authorization header
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }

      // Use our edge function to securely exchange the code for tokens
      const { data, error } = await supabase.functions.invoke('google-oauth-exchange', {
        body: {
          code,
          redirectUri,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to exchange code for tokens');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to get tokens');
      }

      // Send tokens to parent window
      window.opener?.postMessage({
        type: 'GOOGLE_OAUTH_SUCCESS',
        tokens: data.tokens
      }, window.location.origin);

      window.close();
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      window.opener?.postMessage({
        type: 'GOOGLE_OAUTH_ERROR',
        error: 'Failed to get access tokens'
      }, window.location.origin);
      window.close();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Connecting your Google Search Console...</p>
      </div>
    </div>
  );
}